import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Loader2 } from "lucide-react";

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

export function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
}: DeleteAccountModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isDeleting) onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, isDeleting, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-md"
        onClick={isDeleting ? undefined : onClose}
        style={{ zIndex: 0 }}
      />
      <div
        className="relative w-full max-w-sm mx-4 rounded-2xl border border-white/40 bg-white/95 backdrop-blur-xl p-6 shadow-2xl shadow-black/15 animate-modal-in text-center"
        style={{ zIndex: 1 }}
      >
        <div className="size-12 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="size-5 text-red-400" />
        </div>
        <h3 className="text-[15px] font-bold text-[#2d3127] mb-1">Delete your account?</h3>
        <p className="text-xs text-[#8a8570] mb-5 leading-relaxed">
          This will permanently delete your account and sign you out.
          Your generated content will no longer be accessible.
        </p>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="btn-glass flex-1 py-2.5 text-[13px] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 py-2.5 rounded-lg bg-red-50 border-2 border-red-200 text-red-600 text-[13px] font-semibold hover:bg-red-100 disabled:opacity-70 transition-colors flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Deleting…
              </>
            ) : (
              "Delete my account"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
